import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { updatePaymentRequirementSettings } from '@user/store/closingRequirements/api'
import { ClosingPaymentRequirementsModel } from '@user/store/closingRequirements/typings'
import { useAppDispatch } from '@user/store/hooks'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from '@sales-center/components'
import { useGetPaymentRequirementsOnMount } from '../../hooks/useGetPaymentRequirementsOnMount'
import { CaptureCardRequirement } from './CaptureCardRequirement'
import CardIcon from '../../atoms/Icon/CardIcon'
import { RequirementsHeader } from '../RequirementsHeader'
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'
import { ClosingRequirementsForm } from '@user/components/Settings/Home/ClosingRequirementsForm'

interface Props {
  onClose: () => void
}

export const CaptureCardRequirementForm: React.FC<Props> = (props: Props) => {
  const { onClose } = props
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useAppDispatch()

  const [requirements, setRequirements] = useState<
    ClosingPaymentRequirementsModel[]
  >([])

  const { paymentRequirementsStatus, paymentRequirements } =
    useGetPaymentRequirementsOnMount()

  const onSelected = useCallback(
    (model: ClosingPaymentRequirementsModel) => {
      const items = requirements.filter(
        (x) => x.salesFunnelId != model.salesFunnelId,
      )
      setRequirements(items.concat(model))
    },
    [requirements, setRequirements],
  )

  useEffect(() => {
    if (paymentRequirementsStatus === 'success') {
      setRequirements(paymentRequirements)
    }
  }, [paymentRequirements, paymentRequirementsStatus])

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)
    const result = await dispatch(
      updatePaymentRequirementSettings(requirements),
    )

    if (updatePaymentRequirementSettings.fulfilled.match(result)) {
      setIsLoading(false)
      toast.success('Capture Card Closing Requirements have been updated.')
    } else {
      setIsLoading(false)
      toast.error(
        'We are unable to update Capture Card Closing Requirements at this time.',
      )
    }
    setTimeout(() => {
      onClose()
    }, 0)
  }, [dispatch, onClose, requirements])

  return (
    <ClosingRequirementsForm
      onSave={handleSubmit}
      onCancel={onClose}
      secondaryBtnLabel="Cancel"
      disableSave={isLoading}
      padding="0 20px"
      primaryBtnLabel="Save"
    >
      {paymentRequirementsStatus === 'pending' && (
        <Box
          sx={{
            paddingTop: (theme: Theme) => theme.spacing(4.8),
            textAlign: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {paymentRequirementsStatus === 'fail' && (
        <Box
          sx={{
            paddingTop: (theme: Theme) => theme.spacing(4.8),
            textAlign: 'center',
          }}
        >
          <Alert severity="error">Failed to load Closing Requirements.</Alert>
        </Box>
      )}

      {paymentRequirementsStatus === 'success' && (
        <RequirementsHeader
          title="Closing Requirements for Card Capture"
          subTitle="Prevent sales from being closed/won without a card on file.
              Cards can still be captured after the sale has been closed."
          icon={<CardIcon></CardIcon>}
        ></RequirementsHeader>
      )}

      {paymentRequirements &&
        paymentRequirements.map((x) => (
          <CaptureCardRequirement
            onSelected={onSelected}
            key={x.salesFunnelId}
            {...x}
          ></CaptureCardRequirement>
        ))}
    </ClosingRequirementsForm>
  )
}
